import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function IndustriesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const industryStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Industry-Specific AI Marketing Solutions",
    "provider": {
      "@type": "Organization",
      "name": "Technelle",
      "url": "https://technelle.ai"
    },
    "areaServed": {
      "@type": "State",
      "name": "Wisconsin"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Industry Marketing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Hospitality Marketing",
            "description": "AI-powered marketing solutions for hotels, resorts, and event venues in Wisconsin"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Food & Beverage Marketing",
            "description": "Marketing strategies for restaurants, cafes, and food manufacturers"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Industrial Marketing",
            "description": "B2B marketing for equipment, machinery, and industrial services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Manufacturing Marketing",
            "description": "Marketing solutions for Wisconsin manufacturing companies"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Renewable Energy Marketing",
            "description": "Marketing for solar, wind, and clean tech companies"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Ecommerce Marketing",
            "description": "AI-powered marketing for online retail and DTC brands"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="Wisconsin Industries | AI Marketing | Technelle"
        description="Specialized AI marketing and consulting services for Wisconsin industries including hospitality, manufacturing, renewable energy, ecommerce, and more. Serving businesses across Waukesha, Milwaukee, Madison, and all of Wisconsin with industry-specific marketing strategies."
        keywords="Wisconsin hospitality marketing, manufacturing marketing Wisconsin, renewable energy marketing, ecommerce marketing Wisconsin, Wisconsin industry marketing, B2B marketing Wisconsin, industrial marketing services"
        canonical="https://technelle.ai/industries"
        ogTitle="Wisconsin Industries | AI Marketing | Technelle"
        ogDescription="AI-powered marketing solutions tailored for Wisconsin's key industries. From hospitality to manufacturing, we deliver results for businesses across the state."
        ogUrl="https://technelle.ai/industries"
        structuredData={industryStructuredData}
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Industries', url: 'https://technelle.ai/industries' }
        ]}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle - AI Marketing & Consulting" className="h-14" />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-[#2196F3] transition-colors font-medium">Industries</Link>
            <Link to="/resources" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Resources</Link>
          </div>
          <button 
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
            aria-label="Book free strategy call"
          >
            Book Free Strategy Call
          </button>
        </div>
      </nav>

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                <button 
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close contact form"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <p className="text-[15px] text-gray-600">Please reach out to schedule your free strategy call at <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a> or <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/77fedf7f16208de18a2c56bb4d3d52ca.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="1000"
            height="1200"
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-[56px] sm:text-[68px] lg:text-[76px] leading-[1.1] font-bold text-[#1a1a1a] mb-6 font-serif">
              Industries We <span className="relative inline-block text-[#2196F3]">
                Serve
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 8C100 2 200 2 298 8" stroke="#2196F3" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
            <p className="text-[20px] sm:text-[24px] text-[#1a1a1a]/95 max-w-3xl mx-auto leading-relaxed">
              Tailored AI-powered marketing solutions for diverse Wisconsin business sectors and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[18px] text-gray-600 max-w-3xl mx-auto leading-relaxed">
                We bring 20+ years of marketing expertise and practical AI implementation to Wisconsin businesses across multiple sectors. Here's how we help solve the unique challenges facing industries throughout Waukesha, Milwaukee, Madison, and across the state.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10">
              {/* Hospitality */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-hotel-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Hospitality</h3>
                    <p className="text-[15px] text-gray-600">Hotels, Resorts, Event Venues</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    Seasonal booking fluctuations, high customer acquisition costs, managing online reputation across multiple platforms, and competing with OTAs for direct bookings.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">AI-powered dynamic pricing and demand forecasting for Wisconsin tourism seasons</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Automated guest communication and personalized email campaigns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Reputation management systems and review response automation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Direct booking optimization to reduce OTA commissions</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Food & Beverage */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-restaurant-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Food & Beverage</h3>
                    <p className="text-[15px] text-gray-600">Restaurants, Cafes, Food Manufacturers</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    Thin profit margins, high staff turnover, customer retention challenges, and competing in crowded Wisconsin markets with limited marketing budgets.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Hyper-local SEO and Google Business optimization for Wisconsin locations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Loyalty programs powered by AI-driven customer segmentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Social media content strategies that drive foot traffic</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Automated email and SMS campaigns for repeat business</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Industrial */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Industrial</h3>
                    <p className="text-[15px] text-gray-600">Equipment, Machinery, B2B Services</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    Long sales cycles, complex technical products requiring education, limited digital presence, and difficulty reaching decision-makers in Wisconsin target accounts.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Account-Based Marketing (ABM) strategies for enterprise sales</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Technical content marketing and thought leadership positioning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">LinkedIn advertising and lead generation campaigns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Sales enablement tools and automated nurture sequences</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Manufacturing */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-4-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Manufacturing</h3>
                    <p className="text-[15px] text-gray-600">Production, Supply Chain, Assembly</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    Global competition, pricing pressure, demonstrating value beyond cost, and communicating technical capabilities to non-technical buyers across Wisconsin manufacturing hubs.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help Wisconsin Manufacturers:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Value-based positioning and differentiation strategies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Case studies and ROI calculators for prospect education</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Trade show marketing and event lead capture systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Digital transformation of sales processes and customer portals</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Renewable Energy */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-sun-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Renewable Energy</h3>
                    <p className="text-[15px] text-gray-600">Solar, Wind, Clean Tech</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    Complex ROI calculations, regulatory hurdles, educating Wisconsin prospects on long-term value, and overcoming upfront cost objections.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Educational content marketing and ROI demonstration tools</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Lead nurturing campaigns addressing common objections</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Government incentive and rebate program communication</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Customer success stories and environmental impact reporting</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Ecommerce */}
              <div className="bg-gray-50 rounded-2xl p-10 hover:shadow-xl transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-cart-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Ecommerce</h3>
                    <p className="text-[15px] text-gray-600">Online Retail, DTC Brands</p>
                  </div>
                </div>
                <div className="mb-6">
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">Common Pain Points:</strong>
                  <p className="text-[15px] text-gray-700 leading-relaxed">
                    High cart abandonment rates, customer acquisition cost increases, difficulty standing out in crowded markets, and building brand loyalty online for Wisconsin-based retailers.
                  </p>
                </div>
                <div>
                  <strong className="text-[16px] text-[#1a1a1a] block mb-3">How We Help:</strong>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">AI-powered product recommendations and personalization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Abandoned cart recovery and email automation flows</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Conversion rate optimization and A/B testing programs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-[14px] text-gray-700">Multi-channel attribution modeling and performance analytics</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Trades & Contractors — Featured */}
              <div className="lg:col-span-2 bg-[#1a1a1a] rounded-2xl p-10 text-white">
                <div className="flex flex-col lg:flex-row gap-10 items-start">
                  <div className="flex-1">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 bg-[#2196F3] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-white text-3xl" aria-hidden="true"></i>
                      </div>
                      <div>
                        <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold mb-1">Featured Vertical</div>
                        <h3 className="text-[28px] font-bold text-white">Trades & Contractors</h3>
                        <p className="text-[15px] text-white/60">HVAC, Plumbers, Electricians, Roofers, Landscapers, Pest Control</p>
                      </div>
                    </div>
                    <p className="text-[16px] text-white/80 leading-relaxed mb-6">
                      Google's AI search has changed how homeowners find contractors. Wisconsin trades businesses are cash-rich and marketing-blind — and the ones who adapt now will own their local market for years.
                    </p>
                    <ul className="space-y-2 mb-8">
                      {[
                        'Local SEO & Google AI search optimization for contractors',
                        'Automated lead capture and 24/7 AI chatbot booking',
                        'Google Business Profile optimization',
                        'Website builds designed to convert homeowners',
                        'Proven results: Tony\'s Heating & Cooling, Waukesha',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                          <span className="text-[14px] text-white/90">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/industries/trades"
                      className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-8 py-3 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
                    >
                      See the Full Trades Page
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </Link>
                  </div>
                  <div className="lg:w-72 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20technician%20in%20uniform%20working%20on%20air%20conditioning%20unit%20outside%20Wisconsin%20suburban%20home%2C%20clean%20simple%20background%2C%20warm%20natural%20lighting%2C%20commercial%20photography%20style%2C%20high%20quality%20realistic%20photo&width=576&height=480&seq=trades-industries-card-001&orientation=portrait"
                      alt="Wisconsin trades contractor"
                      className="w-full h-60 object-cover object-top rounded-xl"
                      loading="lazy"
                      decoding="async"
                      width="576"
                      height="480"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Industries */}
              <div className="lg:col-span-2 bg-gradient-to-br from-[#1565C0] via-[#2196F3] to-[#1565C0] rounded-2xl p-10 text-white">
                <h3 className="text-[28px] font-bold mb-6">Additional Wisconsin Industries We Serve</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-flashlight-line text-2xl" aria-hidden="true"></i>
                      <h4 className="text-[20px] font-semibold">Utilities & Energy Storage</h4>
                    </div>
                    <p className="text-[14px] text-white/90 leading-relaxed">
                      Customer education, regulatory compliance marketing, and smart grid technology adoption campaigns for Wisconsin utilities.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-tools-line text-2xl" aria-hidden="true"></i>
                      <h4 className="text-[20px] font-semibold">Trade Services</h4>
                    </div>
                    <p className="text-[14px] text-white/90 leading-relaxed">
                      Local SEO, reputation management, booking automation, and seasonal campaign optimization for Wisconsin contractors and service providers.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-heart-pulse-line text-2xl" aria-hidden="true"></i>
                      <h4 className="text-[20px] font-semibold">Health & Beauty</h4>
                    </div>
                    <p className="text-[14px] text-white/90 leading-relaxed">
                      Appointment scheduling automation, client retention programs, and social proof marketing strategies for Wisconsin wellness businesses.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-building-2-line text-2xl" aria-hidden="true"></i>
                      <h4 className="text-[20px] font-semibold">Professional Services</h4>
                    </div>
                    <p className="text-[14px] text-white/90 leading-relaxed">
                      Thought leadership positioning, content marketing, and lead generation for Wisconsin consultants, agencies, and B2B services.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-24">
        <div className="container mx-auto px-8 text-center">
          <h2 className="text-[56px] font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
            Don't See Your<br />
            <span className="relative inline-block text-[#2196F3]">
              Industry
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 6C60 2 140 2 198 6" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>?
          </h2>
          <p className="text-[20px] text-gray-600 mb-10 max-w-2xl mx-auto">
            We've worked with Wisconsin businesses across dozens of sectors. Let's talk about your specific challenges and goals.
          </p>
          <button 
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer min-w-[44px] min-h-[44px]"
          >
            <span>Book Your Free Strategy Call</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle" className="h-12" width="48" height="48" loading="lazy" decoding="async" />
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
              <nav className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Home</Link>
                <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">About</Link>
                <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Services</Link>
                <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Industries</Link>
                <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Contact</Link>
              </nav>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
              <div className="space-y-3">
                <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Legal</h5>
              <nav className="space-y-3">
                <Link to="/privacy-policy" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Privacy Policy</Link>
                <Link to="/terms" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Terms</Link>
              </nav>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-[14px] text-gray-400">© 2025 Technelle. All rights reserved.</div>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
